import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../ui/sheet';
import { SwipePager } from '../../ui/swipe-pager';
import { useToast } from '../../ui/use-toast';
import { useAuth } from '../../../hooks/useAuth';
import { roundRefreshOptions, useAutoRefresh } from '../../../hooks/useAutoRefresh';
import { useBreadcrumb } from '../../../hooks/useBreadcrumb';
import { competitionsService } from '../../../services/competitions';
import { competitionRoundsService } from '../../../services/competition-rounds';
import { scoresWholeRound } from '../../../utils/competition-scoring';
import type { Competition, CompetitionCategory, CompetitionParticipant } from '../../../types/competition';
import type { CompetitionRound, RoundClimb } from '../../../types/competition-round';
import { RoundCategoryTabs } from '../RoundCategoryTabs';
import { RoundClock, RoundClockSubject } from '../RoundClock';
import { useRoundTimeUp } from '../round-clock';
import { competitionCrumb } from '../competition-crumb';
import {
  DEFAULT_JUDGE_FILTERS,
  NO_CLASS,
  blockKey,
  judgePagePath,
  landingProblem,
  loadJudgeFilters,
  problemForClass,
  storeJudgeClass,
  storeJudgeFilters,
  wallBlocks,
  type JudgeFilters,
  type WallBlock,
} from './judge-blocks';
import { JudgeBoulderTable } from './JudgeBoulderTable';
import { JudgeLeadTable } from './JudgeLeadTable';
import { WallBlocksList } from './WallBlocksList';

/** One tab of a block: a class, the problem it climbs here, and its athletes. */
interface ClassTab {
  key: string;
  category: CompetitionCategory | null;
  roundClimb: RoundClimb;
  participants: CompetitionParticipant[];
}

/**
 * One block on the wall, as the judge standing at it scores it.
 *
 * A block is a **name** (see `wallBlocks`): the classes that climb "Blocco 3"
 * are its tabs, each one the class's own problem — or the one the whole round
 * shares — and its athletes on this round, and swiping moves between them. A
 * class nobody of is climbing here has no tab, nor has one whose problem was
 * handed to another judge: it is not this judge's to look at.
 *
 * The URL names the problem being scored (and `?category` the class), so the
 * tab picked, or the block picked from the drawer, replaces it rather than
 * adding a step to Back.
 *
 * Search and sort are kept per class for the whole round (`judge-filters`), and
 * the class last scored is what the next block opens on.
 */
export function JudgeBlockShell() {
  const { slug, roundId, roundClimbId } = useParams<{ slug: string; roundId: string; roundClimbId: string }>();
  const [searchParams] = useSearchParams();
  const askedCategory = searchParams.get('category');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();

  const [competition, setCompetition] = useState<Competition | null>(null);
  const [round, setRound] = useState<CompetitionRound | null>(null);
  const [categories, setCategories] = useState<CompetitionCategory[]>([]);
  const [participants, setParticipants] = useState<CompetitionParticipant[]>([]);
  const [loading, setLoading] = useState(true);
  const [picking, setPicking] = useState(false);
  const [filters, setFilters] = useState<Record<string, JudgeFilters>>(() => loadJudgeFilters(slug, roundId));

  /**
   * Whose entries this page lists. Somebody scoring the competition gets the
   * whole round; anybody else is narrowed to their own squads' athletes, so a
   * stray visitor reads an empty table rather than somebody else's start list.
   */
  const wholeRound = scoresWholeRound(user?.staff, slug);

  /**
   * Asked again when the viewer's roles arrive (the profile is refreshed after
   * the page opens), so an answer to the earlier question — narrowed to their
   * own squads, often empty — must not land on top of the later one.
   */
  const load = useCallback(async (superseded: () => boolean) => {
    if (!slug || !roundId) return;
    try {
      const [competitionData, roundData, categoriesData, participantsData] = await Promise.all([
        competitionsService.getCompetitionBySlug(slug),
        competitionRoundsService.getRound(slug, roundId),
        competitionsService.getCategories(slug),
        // Only the entries the desk confirmed, and only this round's people.
        competitionsService.getParticipants(slug, {
          confirmed: true,
          roundId,
          team: wholeRound ? undefined : 'mine',
        }),
      ]);
      if (superseded()) return;
      setCompetition(competitionData);
      setRound(roundData);
      setCategories(categoriesData);
      setParticipants(participantsData);
    } catch {
      if (!superseded()) toast({ title: t('errors.actionFailed', { defaultValue: 'Action failed' }), variant: 'destructive' });
    } finally {
      if (!superseded()) setLoading(false);
    }
  }, [slug, roundId, wholeRound, t, toast]);

  useEffect(() => {
    let stale = false;
    load(() => stale);
    return () => {
      stale = true;
    };
  }, [load]);

  /**
   * The round and its people again, at the competition's pace
   * (roundRefreshOptions), without the skeleton: the
   * panel moves the round on (verification, closed) and the desk confirms late
   * entries while a judge stands at the wall. Each table refreshes its own
   * results (JudgeBoulderTable, JudgeLeadTable).
   */
  const refresh = useCallback(async () => {
    if (!slug || !roundId) return;
    const [roundData, participantsData] = await Promise.all([
      competitionRoundsService.getRound(slug, roundId),
      competitionsService.getParticipants(slug, {
        confirmed: true,
        roundId,
        team: wholeRound ? undefined : 'mine',
      }),
    ]);
    setRound(roundData);
    setParticipants(participantsData);
  }, [slug, roundId, wholeRound]);

  useAutoRefresh(refresh, roundRefreshOptions(round, !loading));

  const handleTimeUp = useRoundTimeUp(setRound);
  // Reaching the start only means "go and look": a failed look is retried by
  // the auto-refresh.
  const handleStartReached = useCallback(() => {
    refresh().catch(() => {});
  }, [refresh]);

  const blocks = useMemo(() => wallBlocks(round?.round_climbs ?? []), [round?.round_climbs]);
  const current = round?.round_climbs?.find((rc) => rc.id === roundClimbId) ?? null;
  const block: WallBlock | null = current ? (blocks.find((b) => b.key === blockKey(current)) ?? null) : null;

  const tabs = useMemo<ClassTab[]>(() => {
    if (!round || !current || !block) return [];
    // The judge is not one of the people they score.
    const climbers = participants.filter((p) => !user || p.user_id !== user.id.toString());
    const roundCategories = categories.filter((c) => round.category_ids.includes(c.id));

    if (roundCategories.length === 0) {
      return [{ key: NO_CLASS, category: null, roundClimb: current, participants: climbers }];
    }

    return roundCategories.flatMap((category) => {
      const roundClimb = problemForClass(block, category.id);
      if (!roundClimb || roundClimb.can_score === false) return [];
      const people = climbers.filter((p) => p.competition_category_id === category.id);
      if (people.length === 0) return [];
      return [{ key: category.id, category, roundClimb, participants: people }];
    });
  }, [round, current, block, participants, categories, user]);

  /**
   * The class on screen: the one the URL names, else the one the problem in the
   * URL belongs to, else the first.
   */
  const active =
    tabs.find((tab) => tab.key === askedCategory) ??
    tabs.find((tab) => tab.roundClimb.id === roundClimbId && tab.key === current?.competition_category_id) ??
    tabs.find((tab) => tab.roundClimb.id === roundClimbId) ??
    tabs[0] ??
    null;

  const goToProblem = useCallback(
    (roundClimb: RoundClimb, categoryKey: string | null) => {
      if (!slug || !round) return;
      const category = categoryKey && categoryKey !== NO_CLASS ? categoryKey : null;
      if (category) storeJudgeClass(slug, round.id, category);
      navigate(judgePagePath(slug, round, roundClimb, category), { replace: true });
    },
    [navigate, round, slug]
  );

  const selectTab = useCallback(
    (key: string) => {
      const tab = tabs.find((candidate) => candidate.key === key);
      if (tab) goToProblem(tab.roundClimb, tab.key);
    },
    [goToProblem, tabs]
  );

  // The URL always names what is on screen, so a reload, or Back from the next
  // page, comes back to it.
  useEffect(() => {
    if (!active || loading) return;
    if (active.roundClimb.id !== roundClimbId || (active.key !== NO_CLASS && active.key !== askedCategory)) {
      goToProblem(active.roundClimb, active.key);
    }
  }, [active, askedCategory, goToProblem, loading, roundClimbId]);

  const pickBlock = (picked: WallBlock) => {
    setPicking(false);
    const preferred = active && active.key !== NO_CLASS ? active.key : null;
    const classOrder = tabs.flatMap((tab) => (tab.category ? [tab.category.id] : []));
    const roundClimb = landingProblem(picked, preferred, classOrder);
    if (!roundClimb) return;
    goToProblem(roundClimb, roundClimb.competition_category_id ?? preferred);
  };

  const changeFilters = (key: string, next: JudgeFilters) => {
    setFilters((all) => {
      const updated = { ...all, [key]: next };
      storeJudgeFilters(slug, roundId, updated);
      return updated;
    });
  };

  const backToRound = () => navigate(`/competitions/${slug}/rounds/${roundId}?view=judge`);

  useBreadcrumb(() => {
    // The stage's logo stands first, where DivePeaks' would.
    const stage = competitionCrumb(competition, () => navigate(`/competitions/${slug}`));
    return {
      onBack: backToRound,
      hideHomeLogo: stage.hideHomeLogo,
      segments: round
        ? [
            ...stage.segments,
            // The block's name is the clock's strip's, under it (RoundClock).
            { label: round.name, onClick: backToRound },
          ]
        : [],
      loading,
    };
  }, [competition, round?.name, loading, slug, roundId]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!round || !current || !block || !slug) {
    return (
      <p className="py-12 text-center text-muted-foreground">
        {t('competitionRounds.notFound', { defaultValue: 'Round not found' })}
      </p>
    );
  }

  // A judge writes through the corrections and the check that follows them;
  // only a closed round is shut to them here.
  const scoringOpen = round.running || round.correcting || round.under_review;
  const classTabs = tabs.length > 1 && !!active;

  const renderTable = (key: string) => {
    const tab = tabs.find((candidate) => candidate.key === key);
    if (!tab) return null;
    const Table = round.round_type === 'Lead' ? JudgeLeadTable : JudgeBoulderTable;
    return (
      <Table
        // A fresh table per problem: its results are its own, and a judge who
        // moves to the next block must not see the last one's for a moment.
        key={tab.roundClimb.id}
        competitionSlug={slug}
        round={round}
        roundClimb={tab.roundClimb}
        participants={tab.participants}
        filters={filters[tab.key] ?? DEFAULT_JUDGE_FILTERS}
        onFiltersChange={(next) => changeFilters(tab.key, next)}
        scoringOpen={scoringOpen}
      />
    );
  };

  return (
    <div className="flex min-h-[80vh] w-full flex-col pb-6">
      {/* The round's state and clock in the strip under the navbar, as on the
          round page, opened by the block's name: the way to the next block, as
          a judge moves between the boulders of a round far more often than
          they go back to it. */}
      <RoundClock
        round={round}
        onTimeUp={handleTimeUp}
        onStartReached={handleStartReached}
        subject={<RoundClockSubject label={block.name} onPick={() => setPicking(true)} />}
      />

      {/* The classes that climb this block, pinned under the navbar. The block's
          picture and the camera are the room's view's (the round page), not
          this one's: here the judge scores. */}
      {classTabs && active && (
        <div
          className="sticky top-[var(--navbar-height,65px)] z-30 -mx-4 -mt-3 mb-4 border-b bg-background px-4 py-2 sm:-mx-6 sm:px-6"
          data-testid="judge-block-bar"
        >
          <RoundCategoryTabs
            categories={tabs.flatMap((tab) => (tab.category ? [tab.category] : []))}
            selectedId={active.key}
            onSelect={selectTab}
          />
        </div>
      )}

      {/* A closed round takes no marks from this app at all, so the table is
          still here to read and nothing on it can be pressed. Corrections after
          this are made from the gym panel. */}
      {round.closed && (
        <div
          className="mb-4 rounded-lg border border-dashed p-3 text-sm text-muted-foreground"
          data-testid="round-closed-notice"
        >
          {t('competitionRounds.closedNotice')}
        </div>
      )}

      {!active ? (
        <p className="py-8 text-center text-muted-foreground" data-testid="no-classes-here">
          {t('judgeBlock.nobodyHere')}
        </p>
      ) : tabs.length > 1 ? (
        <SwipePager
          ids={tabs.map((tab) => tab.key)}
          activeId={active.key}
          onChange={selectTab}
          renderPage={renderTable}
          className="-mx-4 sm:-mx-6"
          pageClassName="px-4 sm:px-6"
        />
      ) : (
        renderTable(active.key)
      )}

      <Sheet open={picking} onOpenChange={setPicking}>
        <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{t('judgeBlock.pickBlock')}</SheetTitle>
          </SheetHeader>
          <WallBlocksList
            blocks={blocks}
            categories={categories}
            onSelect={pickBlock}
            currentKey={block.key}
          />
        </SheetContent>
      </Sheet>

    </div>
  );
}
